import { Box, Chip, Tooltip } from '@mui/material'
import { syncColors } from './theme/tokens.js'

export type SyncState = 'synced' | 'syncing' | 'offline' | 'blocked' | 'conflict'

const LABELS: Record<SyncState, (n: number) => string> = {
  synced: () => 'Synchronisé',
  syncing: (n) => (n > 0 ? `Synchronisation… ${n}` : 'Synchronisation…'),
  offline: (n) => (n > 0 ? `Hors ligne · ${n} en attente` : 'Hors ligne'),
  blocked: (n) => `${n} non envoyée${n > 1 ? 's' : ''}`,
  conflict: (n) => `${n} conflit${n > 1 ? 's' : ''}`,
}

const HINTS: Record<SyncState, string> = {
  synced: 'Toutes vos modifications sont enregistrées sur le serveur.',
  syncing: 'Envoi des modifications en cours.',
  offline:
    'Vous travaillez sur vos données locales. Vos modifications seront envoyées automatiquement au retour de la connexion.',
  blocked:
    'Le serveur a refusé ces modifications : elles n’existent que sur cet appareil et ne repartiront pas seules. Ouvrez Paramètres › Synchronisation pour en connaître la raison.',
  conflict: 'Des modifications concurrentes demandent votre arbitrage.',
}

/**
 * Pastille d'état de synchronisation (W-04).
 *
 * ⚠️ Hors ligne s'affiche en ambre, jamais en rouge : c'est un état normal du
 * modèle local-first, pas une panne. Le rouge est réservé aux conflits, seul
 * cas qui exige une action de l'utilisateur.
 */
export function SyncBadge({
  state,
  pending = 0,
}: {
  state: SyncState
  pending?: number
}): JSX.Element {
  const color = syncColors[state]

  return (
    <Tooltip title={HINTS[state]}>
      <Chip
        size="small"
        variant="outlined"
        // aria-live : un changement d'état doit être annoncé aux lecteurs d'écran.
        aria-live="polite"
        label={LABELS[state](pending)}
        icon={
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: color,
              ml: 1,
              animation: state === 'syncing' ? 'pulse 1.2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.35 },
              },
              // Respecte le réglage système de réduction des animations.
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        }
        sx={{ borderColor: color, color, fontWeight: 500 }}
      />
    </Tooltip>
  )
}
